import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const groups = [
  {
    title: "Contactgegevens",
    subtitle: "Deze gegevens verschijnen op de site en ontvangen formulierinzendingen.",
    fields: [
      { name: "contact_email", label: "Contact e-mailadres (contactformulier)", type: "text" },
      { name: "contact_address", label: "Adres", type: "text" },
      { name: "membership_recipient", label: "E-mailadres voor lidmaatschapsaanvragen", type: "text" },
      { name: "partner_emails", label: "Partner-contactadressen (komma-gescheiden)", type: "text" },
    ],
  },
  {
    title: "Homepage hero",
    subtitle: "De grote titel en tekst op de startpagina.",
    fields: [
      { name: "hero_title_line1", label: "Titel — regel 1", type: "text" },
      { name: "hero_title_line2", label: "Titel — regel 2", type: "text" },
      { name: "hero_subtitle", label: "Subtitel", type: "textarea" },
      { name: "intro_text", label: "Introtekst ('Wie zijn wij')", type: "textarea" },
    ],
  },
  {
    title: "Sociale media",
    subtitle: "Links naar je kanalen (laat leeg om te verbergen).",
    fields: [
      { name: "instagram_url", label: "Instagram URL", type: "text" },
      { name: "linkedin_url", label: "LinkedIn URL", type: "text" },
    ],
  },
];

const KEYS = groups.flatMap((g) => g.fields.map((f) => f.name));

export default function AdminSettings() {
  const { config, refresh } = useSiteConfig();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const init = {};
    KEYS.forEach((k) => (init[k] = config[k] ?? ""));
    setForm(init);
  }, [config]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/config", form);
      await refresh();
      toast.success("Instellingen opgeslagen");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!form) return null;

  return (
    <div data-testid="admin-settings-page" style={{ fontFamily: "'Outfit', sans-serif" }}>
      <div className="mb-8">
        <h1 className="text-2xl font-700 text-[#0B0B0C]">Instellingen</h1>
        <p className="text-sm text-[#0B0B0C]/60 mt-1">Pas de inhoud van de website aan zonder code te wijzigen.</p>
      </div>

      <form onSubmit={save} className="max-w-2xl space-y-8">
        {groups.map((g) => (
          <div key={g.title} className="bg-white border border-[#E2E8F0] rounded-lg p-6">
            <h2 className="text-base font-600 text-[#0B0B0C]">{g.title}</h2>
            <p className="text-xs text-[#0B0B0C]/50 mb-5">{g.subtitle}</p>
            <div className="space-y-4">
              {g.fields.map((f) => (
                <div key={f.name}>
                  <Label htmlFor={f.name}>{f.label}</Label>
                  {f.type === "textarea" ? (
                    <Textarea id={f.name} data-testid={`settings-${f.name}`} rows={3} value={form[f.name]} onChange={set(f.name)} className="mt-1.5" />
                  ) : (
                    <Input id={f.name} data-testid={`settings-${f.name}`} value={form[f.name]} onChange={set(f.name)} className="mt-1.5" />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        <button
          type="submit"
          disabled={saving}
          data-testid="settings-save"
          className="inline-flex items-center gap-2 text-sm font-600 px-6 py-3 rounded-md text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60"
          style={{ background: "#0A3087" }}
        >
          <Save size={16} /> {saving ? "Bezig..." : "Opslaan"}
        </button>
      </form>
    </div>
  );
}
